import { faRefresh, faShoppingCart } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { PaymentOption } from 'src/models/cart/_types';
import { getPaymentOptionDetails } from 'src/models/cart/utils';
import { formatCurrency } from 'src/utils/numberFormatter';

import { usePosContext } from '../PosContext';

interface PaymentOptionSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  item: any;
  paymentOptions: PaymentOption[];
  onSelect: (paymentOption: PaymentOption) => void;
}

export const PaymentOptionSelectionDialog = ({
  open,
  onClose,
  item,
  paymentOptions,
  onSelect,
}: PaymentOptionSelectionDialogProps) => {
  const { isGuestCheckout } = usePosContext();
  const filteredPaymentOptions = useMemo(() => {
    if (isGuestCheckout) {
      return paymentOptions.filter((option) => option.type === 'purchase');
    } else {
      return paymentOptions;
    }
  }, [paymentOptions, isGuestCheckout]);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.paymentOptionSelection',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const { session, isDarkmode } = useOptionalSession();
  const [selectedOption, setSelectedOption] = useState<PaymentOption | null>(
    filteredPaymentOptions[0] || null
  );

  const defaultProductImage = isDarkmode()
    ? session?.darkmodeLogoUrl
    : session?.logoUrl;

  const getImageUrl = () => {
    if (!item) return defaultProductImage;

    if (item.imageUrl) {
      return item.imageUrl;
    } else if (item.smallPlanImage) {
      return item.smallPlanImage;
    } else {
      return defaultProductImage;
    }
  };

  const handleSelect = () => {
    if (selectedOption) {
      onSelect(selectedOption);
      onClose();
    }
  };

  const getPaymentOptionIcon = (option: PaymentOption) => {
    switch (option.type) {
      case 'subscription':
        return faRefresh;
      case 'purchase':
      default:
        return faShoppingCart;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: tCommon('cancel'),
          onClick: onClose,
          variant: 'bordered',
        },
        {
          label: t('addToCart'),
          onClick: handleSelect,
          variant: 'prominent',
          disabled: !selectedOption,
        },
      ]}
    >
      <Stack spacing={3}>
        {/* Product Info */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="img"
            src={getImageUrl()}
            alt={item?.name}
            sx={{
              width: 60,
              height: 60,
              borderRadius: 1,
              objectFit: 'cover',
            }}
          />
          <Stack>
            <Typography variant="h6">{item?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('selectPaymentOption')}
            </Typography>
          </Stack>
        </Stack>

        {/* Payment Options */}
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('paymentOptions')}
          </Typography>
          {filteredPaymentOptions.map((option) => (
            <ListItem
              key={option.id || `${option.type}-${option.amount}`}
              clickable
              rounded
              selected={selectedOption === option}
              onClick={() => setSelectedOption(option)}
              leftAdornment={
                <Icon
                  icon={getPaymentOptionIcon(option)}
                  sx={{ marginRight: 2 }}
                />
              }
              rightAdornment={
                selectedOption === option && <Icon icon={faCheck} />
              }
              primary={option.name || getPaymentOptionDetails(option)}
              secondary={
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(option.amount, {
                      currency: option.currency || session?.defaultCurrency,
                      cents: true,
                    })}
                    {option.type === 'subscription' &&
                      option.interval &&
                      ` / ${option.intervalCount || 1} ${option.interval}${
                        (option.intervalCount || 1) > 1 ? 's' : ''
                      }`}
                  </Typography>
                  {option.trialPeriodDays && option.trialPeriodDays > 0 && (
                    <Typography variant="caption" color="primary.main">
                      {t('trialPeriod', { days: option.trialPeriodDays })}
                    </Typography>
                  )}
                </Stack>
              }
            />
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
};
